import config from 'config';
import axios from 'axios';
import utils from './utils';

const API_END_POINT = config.api.api;

class Client {
  constructor(opt = {}) {
    this.opt = Object.assign(this._getDefaultOption(), opt);
    this.request = axios.create(this.opt);
    this.isRefreshed = false;
    this.requestsToRefresh = [];
    this.retryCountMap = new Map(); // Track retry count for each request
    this.MAX_RETRY_COUNT = 1; // Maximum number of retries per request
    this._interceptors();
  }

  _interceptors() {
    this.request.interceptors.response.use((response) => response,
      (error) => {
        const { response, config } = error;
        const status = response?.status;
        const message = response?.data?.message;
        const statusText = response?.statusText;
        const url = config?.url || '';
        

        // Endpoints that should NOT be retried on 401 (to prevent infinite loops)
        const SKIP_RETRY_ENDPOINTS = ['/user/remove', '/mvp/remove-user'];
        const shouldSkipRetry = SKIP_RETRY_ENDPOINTS.some(endpoint => url.includes(endpoint));
        
        if (shouldSkipRetry) {
          return Promise.reject(error);
        }

        // Check retry count for this specific request
        const retryCount = this.retryCountMap.get(url) || 0;
        
        if (status === 401 && (statusText === 'Unauthorized' || message === 'Unauthenticated request') && !url.includes('/mvp/sign-in')) {
          // Check if we've exceeded max retry count
          if (retryCount >= this.MAX_RETRY_COUNT) {
            this.retryCountMap.delete(url); // Clean up
            return Promise.reject(error);
          }

          // Increment retry count
          this.retryCountMap.set(url, retryCount + 1);

          if (!this.isRefreshed) {
            this.isRefreshed = true;

            this
              .refreshToken()
              .then((accessToken) => {
                this.requestsToRefresh.forEach((callback) => {
                  callback(accessToken);
                });
              })
              .catch((error) => {
                this.requestsToRefresh.forEach((callback) => callback(null));
              })
              .finally(() => {
                this.isRefreshed = false;
                this.requestsToRefresh = [];
              });
          }

          return new Promise((resolve, reject) => {
            this.requestsToRefresh.push((token) => {
              if (token) {
                let request = { ...config }
                delete request.headers.Authorization;
                delete request.headers.authorization;
                request.headers['Authorization'] = token;
                
                // Retry the request - if it fails again with 401, the retry count will prevent infinite loop
                this.request(request)
                  .then((response) => {
                    // Success - clean up retry count
                    this.retryCountMap.delete(url);
                    resolve(response);
                  })
                  .catch((retryError) => {
                    // If retry also fails, clean up and reject
                    this.retryCountMap.delete(url);
                    reject(retryError);
                  });
              } else {
                this.retryCountMap.delete(url); // Clean up on failure
                reject(error);
              }
            });
          });
        }
        
        // For non-401 errors or after max retries, clean up and reject
        if (retryCount > 0) {
          this.retryCountMap.delete(url);
        }
        return Promise.reject(error);
      },
    );
  }

  async refreshToken() {
    const refreshToken = await utils.getCookie('_RFT');
    if (!refreshToken) {
      return null;
    }
    const headers = { 'authorization': refreshToken };
    try {
      const data = await this.sendRequest({
        path: `/mvp/refresh-token`,
        method: 'POST',
        headers: headers,
      });

      if (data.status === 'OK') {
        const user = {
          type: data.user.type,
          mvpUserType: data.user.mvpUserType,
          name: data.user.name,
          _id: data.user._id,
          accessToken: data.accessToken,
        };

        localStorage.setItem('ced:user-info', JSON.stringify(user));
        utils.saveToCookie('_RFT', data.refreshToken, 365);

        return data.accessToken;
      } else {
        return null;
      }
    } catch (error) {
      return null;
    }
  }

  // @private
  _getDefaultOption() {
    return {
      timeout: 60000,
    };
  }

  _initSocket(token) {
    if (this.sock) this.sock.close();
  }

  // @private
  _reset() {
    this.request = axios.create(this._getDefaultOption());
  }

  // @private
  _update(opt) {
    this.request = axios.create(Object.assign(this.opt, opt));
  }

  api(path, method = 'get', params = {}, headers) {
    const language = localStorage.getItem('ced:language') || "vi"
    if (headers && headers.headers) {
      headers.headers['Accept-Language'] = language
    } else {
      headers = {
        headers: {
          'Accept-Language': language
        }
      }
    }
    return new Promise((resolve, reject) => {
      if (!path) {
        // path is required
        return reject(new Error('Used: api(path, method, params)'));
      }

      const originalPath = path;
      if (path.indexOf('http') !== 0) {
        // add '/' to path if missing
        if (path[0] !== '/') {
          path = '/' + path;
        }

        // use v1 version
        path = API_END_POINT + path;

        const timestamp = Date.now();
        if (path.indexOf('?') !== -1) {
          path += '&_t=' + timestamp;
        } else {
          path += '?_t=' + timestamp;
        }
      }

      // support shorthand api(path, params)
      // method will default to get
      if (arguments.length === 2 && String(method) === '[object Object]') {
        params = method;
        method = 'get';
      }

      method = method.toLowerCase();
      if (['get', 'post', 'put', 'delete'].indexOf(method) === -1) {
        return reject(new Error(`ERR: ${method} not allowed, only allow get, post, put, delete`));
      }
      this.request[method.toLowerCase()](path, params, headers)
        .then((res) => {
          resolve(res);
        })
        .catch((err) => {
          reject(err);
        });
    });
  }

  async sendRequest({ path, method, params, headers }) {
    // Get language: try localStorage first (updated when language changes), then sessionStorage, then default to 'vi'
    // localStorage should be updated in App.js when language changes
    const language = localStorage.getItem('ced:language') || sessionStorage.getItem('ced:session-language') || 'vi';
    
    if (headers) {
      headers['Accept-Language'] = language
    } else {
      headers = {
        'Accept-Language': language
      }
    }
    if (method.toLowerCase() === 'get') {
      params = { params, headers };
    }
    try {
      const res = await this.api(path, method, params, { headers });
      return res.data;
    } catch (err) {
      const { response } = err;
      const data = response
        ? {
          ...response.data,
          statusCode: response.status,
        }
        : {
          statusCode: 500,
          message: err.message,
        };

      return Promise.reject(data);
    }
  }
}

export default new Client();
